'use client';
import dynamic from 'next/dynamic';
import React, {
    createRef,
    ReactNode,
    RefObject,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { numberWithCommas } from '@/utils';
import styles from './styles.module.scss';
import { useAnalytics } from '@/integrations';
import { useTranslation, useWindowDimensions } from '@/hook';
import {
    get3dImperfections,
    setImperfectionsImages,
    setImperfectionsImagesCategories,
} from '@/redux/reducers/gallery.reducer';
import { useAppSelector } from '@/hook/redux-hooks';
import {
    FaultyCheckMark,
    FixedMark,
    imgs,
    ImperfectionCheckMark,
} from '../../../public/Images';
import { CarTypeBannerMobile } from './CarProfileSections/CarTypeBanner';
import { CarTypesEnum, getCarType } from './car-type';
import Image from 'next/image';
import {
    AppDispatch,
    CarDetailsType,
    GetCarListing,
    ImperfectionReportItem,
    ImperfectionReportSection,
    ImperfectionReportSectionGrouped,
    ImperfectionStatusEnum,
    IQCReportUIProperty,
    LenderWithInterestRates,
} from '@/types';
import { useDispatch } from 'react-redux';
import { formatDate, getCarCardLocale } from '@/utils';
import { CarProfileContext } from '@/contexts/carProfile.context';
import CarSoldInfoBoxMobile from './CarProfileSections/SimilarCars/CarSoldInfoBoxMobile';
import { AVAILABLE_LANGUAGES, ServiceName } from '@/constants';
import { DimensionsTargets } from '@/hook/use-window-width.hook';
import ReserveInfoBox from './ReserveInfoBox/reserve-info-box.component';
import VidTestimonialsSection from '@/components/testimonialVidSection/vid-testimonials.component';
import { SourceScreen } from '@/types/source-screens.types';
import { carDetailsService } from '@/api-requests';
import { useParams } from 'next/navigation';
import { setSelectedCar } from '@/redux/reducers/checkoutReducer';
import Box from '../shared/Box/Box';
import CarGallery from './CarProfileSections/CarGallery/CarGallery';
import CarInfo from './CarProfileSections/CarInfoSection/car-info.component';
import { SimilarCarsSection } from './CarProfileSections/SimilarCars/SimilarCarsSection';
import CarCTAs from './CarProfileSections/CarCTAs';
import { useSectionObservers } from '@/hook/use-section-observers';
import { useSectionRefs } from '@/hook/use-section-refs';
import { carProfileObserveSections, carProfileSerctions } from './constants';
import { SylndrHelpSection } from './CarProfileSections/SylndrHelpSection';
import { PurchaseCarSteps } from '../purchaseCarSteps';
import { useVerticalOffset } from '@/hook/use-vertical-offset.hook';

const SimilarCarsContainer = dynamic(
    () =>
        import('./CarProfileSections/SimilarCars/SimilarCarsContainer').then(
            mod => mod.default
        ),
    {
        ssr: false,
    }
);

const CarTabs = dynamic(
    () =>
        import('./CarProfileSections/CarTabs/CarTabs').then(mod => mod.default),
    {
        ssr: false,
    }
);

export default function CarDetailsComponent({
    lang,
    carDetails,
    lenders,
}: {
    lang: AVAILABLE_LANGUAGES;
    carDetails: CarDetailsType;
    lenders: LenderWithInterestRates[];
}) {
    const { carId } = useParams();
    const [imperfectionDataGroupped, setImperfectionDataGroupped] =
        useState<ImperfectionReportSectionGrouped | null>(null);
    const [similarCars, setSimilarCars] = useState<GetCarListing | null>(null);
    const { t, IS_RTL } = useTranslation();
    const [value, setValue] = useState(0);
    const [shouldRenderModals, setShouldRenderModals] = useState(false);
    const { IS_DESKTOP } = useWindowDimensions(DimensionsTargets.MOBILE);
    const [framesWidth, setFramesWidth] = useState(500);
    const [framesHeight, setFramesHeight] = useState(400);
    const sectionObservers = useSectionObservers(carProfileObserveSections);
    const sectionsRefs = useSectionRefs(carProfileSerctions);

    // Get car type using the centralized function
    const carType = getCarType(carDetails);

    // Extract Refs ============================================================================
    const {
        videoSectionRef,
        videoSectionInView,
        similarCarsSectionRef,
        similarCarsSectionInView,
        featuredGridRef,
        featuredGridInView,
        imperfectionSectionRef,
        imperfectionSectionInView,
        carOverviewSectionRef,
        carOverviewSectionInView,
        purchaseStepsSectionRef,
        purchaseStepsSectionInView,
    } = sectionObservers;
    const { mainRef, galleryRef, infoRef, similarCarsStaticRef } = sectionsRefs;

    const carCardLocalizations = getCarCardLocale();
    const [userInterActed, setUserInterActed] = useState(false);
    const [scrollToPosition, setScrollToPosition] = useState<number>(0);
    const { trackEvent } = useAnalytics();
    const [opacity, setOpacity] = useState<number>(1);
    const [openGrouppedImprefectionModal, setOpenGrouppedImprefectionModal] =
        useState(false);
    const [mobileTabs, setMobilesTabs] = useState<string[]>([]);
    const [isBannerSticky, setIsBannerSticky] = useState<boolean>(false);
    const tabsRef = useRef<RefObject<HTMLDivElement>[]>(
        Array.from({ length: IS_DESKTOP ? 5 : (6 as number) }, () =>
            createRef()
        )
    );
    const dispatch = useDispatch<AppDispatch>();

    // ==========================================================================================
    useEffect(() => {
        fetchCarData();
    }, [carId, lang]);

    useEffect(() => {
        setTimeout(() => {
            setShouldRenderModals(true);
        }, 1000);
    }, []);

    useEffect(() => {
        if (IS_DESKTOP && framesWidth !== 800) {
            setFramesWidth(800);
            setFramesHeight(800);
        } else if (!IS_DESKTOP && framesWidth !== 500) {
            setFramesWidth(500);
            setFramesHeight(400);
        }
    }, [IS_DESKTOP]);

    const hasSimilarCars = similarCars && similarCars.data.length > 0;

    useEffect(() => {
        if (!carDetails) {
            return;
        }
        trackEvent('Screen: Car Profile', {
            carName: carDetails.sfVehicleName,
            make: carDetails.make,
            model: carDetails.model,
            year: carDetails.year,
            trim: carDetails.trim?.en,
            kilometers: carDetails.kilometers,
            price: carDetails.sylndrSellingPrice,
            status: carDetails.isPurchaseInProgress
                ? 'Being Sold'
                : carDetails.isReserved
                ? 'Reserved'
                : 'Not Reserved',
            monthlyInstallment: carDetails.installmentPrice,
            transmission: carDetails.transmission,
            newlyAdded:
                carDetails.isNewlyAdded &&
                !(carDetails.isReserved || carDetails.isPurchaseInProgress),
            carLink: `${window.location.origin}/${lang}/car-details/${carDetails.id}`,
            isRefurbished: carDetails.isRefurbished,
        });
    }, [carDetails]);

    useEffect(() => {
        dispatch(get3dImperfections(carDetails.id));
        dispatch(setSelectedCar(carDetails));
        handleInitialTabsLogic();

        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [carDetails, IS_DESKTOP]);

    useEffect(() => {
        window.addEventListener('scroll', listenScrollEvent);
        setTimeout(() => {
            window.scrollTo({ top: 1 });
        }, 0);
        setScrollToPosition(similarCarsStaticRef?.current?.offsetTop || 0);
        return () => {
            window.removeEventListener('scroll', listenScrollEvent);
        };
    }, []);

    useEffect(() => {
        if (imperfectionDataGroupped) {
            intiateImperfectionsReportData(imperfectionDataGroupped);
        }
    }, [imperfectionDataGroupped]);

    const offset = useVerticalOffset(['mobile-car-tabs']);

    // Make banner sticky at bottom when CarGuarantee component comes into view
    useEffect(() => {
        if (!IS_DESKTOP) {
            let hasReachedSection = false;

            const handleBannerScroll = () => {
                const carGuaranteeElement = document.querySelector(
                    '[data-car-guarantee]'
                );
                if (carGuaranteeElement) {
                    const rect = carGuaranteeElement.getBoundingClientRect();
                    // Exact detection - element should be at the top of the screen
                    const isAtTop = rect.top <= offset * 2 && rect.bottom >= 0;

                    // Once the section reaches the top, keep the banner visible
                    if (isAtTop) {
                        hasReachedSection = true;
                        setIsBannerSticky(true);
                    }
                    // Only hide if we scroll back up and the section is no longer at the top
                    else if (hasReachedSection && rect.top > offset * 2) {
                        hasReachedSection = false;
                        setIsBannerSticky(false);
                    }
                }
            };

            window.addEventListener('scroll', handleBannerScroll);
            return () => {
                window.removeEventListener('scroll', handleBannerScroll);
            };
        }
    }, [IS_DESKTOP, offset]);
    // ==========================================================================================
    const carDetailsOverview = useMemo(() => {
        return [
            IS_RTL ? carDetails?.trim?.ar : carDetails?.trim?.en,
            t('kilometersWithValue', {
                value: numberWithCommas(carDetails?.kilometers),
            }),
            t('engineCapacityWithValue', {
                value: carDetails?.engineCapacity,
            }),
            t(carDetails?.color),
            t(carDetails?.transmission),
            t('licenseValidToWithValue', {
                value: formatDate(carDetails?.liscenseValidityDate),
                interpolation: {
                    escapeValue: false,
                },
            }),
        ].filter(Boolean);
    }, [IS_RTL, carDetails]);

    const carInfo = useMemo(() => {
        return {
            id: carDetails?.id,
            make: t(carDetails?.make),
            model: carDetails?.model,
            year: carDetails?.year,
            trim: carDetails?.trim,
            kilometers: carDetails?.kilometers,
            transmission:
                carDetails?.transmission === 'automatic'
                    ? t('automatic')
                    : t('manual'),
            sylndrSellingPrice: carDetails?.sylndrSellingPrice,
            discountPrice: carDetails?.discountPrice,
            installmentPrice: carDetails?.installmentPrice,
            isReserved: carDetails?.isReserved,
            carName: carDetails?.sfVehicleName,
            isSold: carDetails?.isSold,
            isPublished: carDetails?.isPublished,
            isPurchaseInProgress: carDetails?.isPurchaseInProgress,
            showroom: carDetails?.showroom,
            registrationFees: carDetails?.registrationFees,
            bookingType: carDetails?.bookingType,
            isNewlyAdded: carDetails?.isNewlyAdded,
            isRefurbished: carDetails?.isRefurbished,
        };
    }, [carDetails]);
    // ==========================================================================
    async function fetchCarData() {
        const [imperfectionRes, similarCarsRes] = await Promise.all([
            carDetailsService.getImperfectionReportGroupped({
                carId: carId as string,
                lang,
            }),
            carDetailsService.getSimilarCars({
                carId: carId as string,
                lang,
            }),
        ]);
        setImperfectionDataGroupped(imperfectionRes.data);
        setSimilarCars(similarCarsRes);
    }

    const fireEvent = () => {
        if (!userInterActed) {
            setUserInterActed(true);
            trackEvent('Action: Gallery', {
                interaction: true,
                carName: carDetails?.sfVehicleName,
                isRefurbished: carDetails?.isRefurbished,
            });
        }
    };

    const debounce = (func: any, delay: number) => {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func(...args), delay);
        };
    };

    const listenScrollEvent = debounce(() => {
        if (!similarCarsStaticRef) return;
        const y = similarCarsStaticRef?.current?.offsetTop || 0;
        const diff: number = y - 700 - window.scrollY;
        const shouldBeginTransition: boolean = diff < 100 && diff >= 0;
        const shouldEndTransition: boolean = diff <= 0;
        setScrollToPosition(similarCarsStaticRef?.current?.offsetTop || 0);
        if (shouldEndTransition) {
            return setOpacity(0);
        } else if (shouldBeginTransition) {
            setOpacity(diff / 100);
        } else {
            setOpacity(1);
        }
    }, 300);

    const getDimensions = (ele: HTMLDivElement) => {
        const { height } = ele.getBoundingClientRect();
        const offsetTop = ele.offsetTop;
        const offsetBottom = offsetTop + height;
        return {
            height,
            offsetTop,
            offsetBottom,
        };
    };

    const handleScroll = () => {
        if (!mainRef?.current || !galleryRef?.current || !infoRef?.current)
            return;
        const { height: galleryHeight } =
            galleryRef.current.getBoundingClientRect();
        const { height: infoHeight } = infoRef.current.getBoundingClientRect();
        const UPPER_MARGIN_HEIGHT = IS_DESKTOP
            ? galleryHeight
            : galleryHeight + infoHeight + 60;
        const scrollPosition = IS_DESKTOP
            ? window.scrollY - UPPER_MARGIN_HEIGHT + 24
            : window.scrollY;

        tabsRef?.current?.forEach((ref, index) => {
            const ele = ref.current;

            if (ele) {
                const absoluteTop =
                    ele.getBoundingClientRect().top + window.scrollY;
                const { offsetBottom, offsetTop } = getDimensions(ele);

                if (
                    (!IS_DESKTOP && scrollPosition + 120 > absoluteTop) ||
                    (IS_DESKTOP &&
                        scrollPosition < offsetBottom &&
                        scrollPosition > offsetTop)
                ) {
                    setValue(index);
                }
            }
        });
    };

    const handleInitialTabsLogic = () => {
        const tabs = [
            ...(!IS_DESKTOP ? [t('Gallery'), t('priceAndInstallment')] : []),
            t('Benefits'),
            ...(carType !== CarTypesEnum.Dealer ? [t('carSummary')] : []),
            t('Inspection Report'),
            t('Financing'),
            t('Car Features'),
            ...(!IS_DESKTOP ? [t('Similar Cars')] : []),
        ];

        setMobilesTabs(tabs);
        tabsRef.current = Array.from({ length: tabs.length as number }, () =>
            createRef()
        );
    };

    const getSectionRefIndex = (key: string) => {
        return mobileTabs.findIndex(item => item === key);
    };

    const imprefections3d = useAppSelector(
        state => state.gallery.imperfections3d ?? []
    );

    const getIconFromStatus = (status: ImperfectionStatusEnum): ReactNode => {
        switch (status) {
            case ImperfectionStatusEnum.Faulty:
                return (
                    <Image
                        src={FaultyCheckMark}
                        width="20"
                        height="20"
                        alt="Faulty"
                    />
                );
            case ImperfectionStatusEnum.UnFaulty:
                return (
                    <Image
                        src={ImperfectionCheckMark}
                        width="20"
                        height="20"
                        alt="Good"
                    />
                );
            case ImperfectionStatusEnum.Fixed:
                return (
                    <Image src={FixedMark} width="20" height="20" alt="Fixed" />
                );
            default:
                return null;
        }
    };

    const intiateImperfectionsReportData = (
        ImpReport: ImperfectionReportSectionGrouped
    ) => {
        const imperfectionsGalleryCategories: any[] = [];
        const imperfectionsGallery: any[] = [];

        const processSection = (section: ImperfectionReportSection) => {
            const properties: IQCReportUIProperty[] = [];
            section.items.forEach((reportItem: ImperfectionReportItem) => {
                const propertyObj = {
                    id: reportItem.imperfection_item_id,
                    name: IS_RTL ? reportItem.name_ar : reportItem.name_en,
                    status: reportItem.status as ImperfectionStatusEnum,
                    value: getIconFromStatus(reportItem.status),
                    comment: IS_RTL
                        ? reportItem.comment_ar
                        : reportItem.comment_en,
                    tags: reportItem?.tags?.map(tag =>
                        IS_RTL ? tag.tag_ar : tag.tag_en
                    ),
                    images: reportItem.images,
                };

                if (reportItem.images && reportItem.images.length > 0) {
                    const categoryObj = {
                        name: IS_RTL ? section.name_ar : section.name_en,
                        icon: (
                            <Image
                                width={36}
                                height={36}
                                className="object-contain"
                                src={section?.section_good_icon}
                                alt={section.name_en}
                            />
                        ),
                    };

                    let categoryIndex =
                        imperfectionsGalleryCategories.findIndex(
                            category => category.name === categoryObj.name
                        );

                    if (categoryIndex === -1) {
                        categoryIndex = imperfectionsGalleryCategories.length;
                        imperfectionsGalleryCategories.push(categoryObj);
                    }

                    const toBeAddedImages = reportItem.images.map(image => ({
                        newGallery: carDetails?.newGallery,
                        original: image,
                        thumbnail: image,
                        name: IS_RTL ? reportItem.name_ar : reportItem.name_en,
                        comment: IS_RTL
                            ? reportItem.comment_ar
                            : reportItem.comment_en,
                        tags: reportItem?.tags?.map(tag =>
                            IS_RTL ? tag.tag_ar : tag.tag_en
                        ),
                        category: categoryIndex,
                        sectionImg: reportItem?.highlightedImages?.[0] ?? null,
                    }));

                    imperfectionsGallery.push(...toBeAddedImages);
                }

                properties.push(propertyObj);
            });

            return properties;
        };

        if (ImpReport.exterior) {
            ImpReport.exterior.forEach(section => processSection(section));
        }

        if (ImpReport.faulty) {
            ImpReport.faulty.forEach(section => processSection(section));
        }

        dispatch(
            setImperfectionsImagesCategories(imperfectionsGalleryCategories)
        );
        dispatch(setImperfectionsImages(imperfectionsGallery));
    };

    return (
        <div
            className={`${
                openGrouppedImprefectionModal && '!overflow-y-hidden'
            }`}
        >
            <CarProfileContext.Provider
                value={{
                    scrollToPosition,
                    setScrollToPosition,
                }}
            >
                <div className="md:hidden">
                    <CarTypeBannerMobile
                        type={carType}
                        isTop={true}
                        lang={lang}
                    />
                </div>
                <div className="md:px-[5%] px-4 pb-[3vw]">
                    <div className={'flex flex-col gap-3'}>
                        {(carDetails?.isSold ||
                            carDetails?.isPurchaseInProgress) &&
                            !IS_DESKTOP && (
                                <CarSoldInfoBoxMobile
                                    type={
                                        carDetails?.isPurchaseInProgress
                                            ? 'PURCHASE_IN_PROGRESS'
                                            : 'SOLD'
                                    }
                                />
                            )}
                        {carDetails.isReserved &&
                            !carDetails.isPurchaseInProgress && (
                                <ReserveInfoBox />
                            )}
                        <div
                            className={styles.layout}
                            ref={
                                tabsRef?.current[
                                    getSectionRefIndex(t('Gallery'))
                                ]
                            }
                        >
                            <CarGallery
                                ref={galleryRef}
                                carDetails={carDetails}
                                IS_DESKTOP={IS_DESKTOP}
                                framesWidth={framesWidth}
                                framesHeight={framesHeight}
                                imprefections3d={imprefections3d}
                                fireEvent={fireEvent}
                                shouldRenderModals={shouldRenderModals}
                            />
                            <Box
                                ref={infoRef}
                                className="block md:sticky md:top-20 w-full h-fit grid-area-mn"
                            >
                                {
                                    <CarInfo
                                        priceAndInstallmentRef={
                                            tabsRef?.current[
                                                getSectionRefIndex(
                                                    t('priceAndInstallment')
                                                )
                                            ]
                                        }
                                        IS_DESKTOP={IS_DESKTOP}
                                        carDetails={carDetails}
                                        carDetailsOverview={carDetailsOverview}
                                        inspectionRef={
                                            tabsRef?.current[
                                                getSectionRefIndex(
                                                    t('Inspection Report')
                                                )
                                            ]
                                        }
                                        carSummaryRef={
                                            tabsRef?.current[
                                                getSectionRefIndex(
                                                    t('carSummary')
                                                )
                                            ]
                                        }
                                        includesRef={
                                            tabsRef?.current[
                                                getSectionRefIndex(
                                                    t('Benefits')
                                                )
                                            ]
                                        }
                                        highlights={carDetails?.highlights}
                                        isRepainted={
                                            imperfectionDataGroupped?.isRepainted
                                        }
                                        numberOfRepaintedPanels={
                                            imperfectionDataGroupped?.numberOfRepaintedPanels
                                        }
                                        carInfo={carInfo}
                                    />
                                }

                                <div className="mt-6 hidden md:block">
                                    {carType === CarTypesEnum.Dealer ? (
                                        <SylndrHelpSection
                                            carType={carType}
                                            lang={lang}
                                            IS_DESKTOP={IS_DESKTOP}
                                        />
                                    ) : (
                                        <PurchaseCarSteps />
                                    )}
                                </div>
                            </Box>
                            <CarTabs
                                setOpenGrouppedImprefectionModal={
                                    setOpenGrouppedImprefectionModal
                                }
                                setValue={setValue}
                                inView={{
                                    carOverviewSectionInView,
                                    imperfectionSectionInView,
                                    purchaseStepsSectionInView,
                                    featuredGridInView,
                                }}
                                refs={{
                                    carOverviewSectionRef,
                                    mainRef,
                                    galleryRef,
                                    infoRef,
                                    tabsRef,
                                    featuredGridRef,
                                    imperfectionSectionRef,
                                    purchaseStepsSectionRef,
                                }}
                                getDimensions={getDimensions}
                                IS_DESKTOP={IS_DESKTOP}
                                carDetails={carDetails}
                                mobileTabs={mobileTabs}
                                getSectionRefIndex={getSectionRefIndex}
                                imperfectionDataGroupped={
                                    imperfectionDataGroupped
                                }
                                t={t}
                                trackEvent={trackEvent}
                                value={value}
                                shouldRenderModals={shouldRenderModals}
                                lang={lang}
                                carInfo={carInfo}
                                openGrouppedImprefectionModal={
                                    openGrouppedImprefectionModal
                                }
                                lenders={lenders}
                                carType={carType}
                            />
                        </div>
                    </div>
                    {(carDetails?.isSold || carDetails?.isPurchaseInProgress) &&
                        similarCars && (
                            <SimilarCarsContainer
                                similarCars={similarCars}
                                opacity={opacity}
                            />
                        )}
                    <Box
                        ref={
                            tabsRef?.current[
                                getSectionRefIndex(t('Similar Cars'))
                            ]
                        }
                    >
                        {hasSimilarCars && (
                            <Box
                                ref={similarCarsStaticRef}
                                className="mt-8 relative md:border-t border-gray-200"
                            >
                                <SimilarCarsSection
                                    ref={similarCarsSectionRef}
                                    similarCars={similarCars?.data}
                                    carCardLocalizations={carCardLocalizations}
                                    lang={lang}
                                    t={t}
                                    imgs={imgs}
                                    isVisible={
                                        similarCarsSectionInView || IS_DESKTOP
                                    }
                                />
                            </Box>
                        )}
                    </Box>
                    <div
                        className={`${
                            hasSimilarCars ? 'mt-16' : 'mt-2 md:mt-6'
                        }`}
                        ref={videoSectionRef}
                    >
                        {(videoSectionInView || IS_DESKTOP) && (
                            <VidTestimonialsSection
                                SourceScreen={SourceScreen.CAR_PROFILE}
                                serviceName={ServiceName.RETAIL}
                            />
                        )}
                    </div>
                </div>
                <div className="md:hidden sticky z-20 bottom-0">
                    {!IS_DESKTOP &&
                        !carDetails.isPurchaseInProgress &&
                        isBannerSticky && (
                            <div className="transition-all duration-500 ease-in-out transform translate-y-0 opacity-100">
                                <CarTypeBannerMobile
                                    type={carType}
                                    isTop={false}
                                    lang={lang}
                                />
                            </div>
                        )}
                    <CarCTAs t={t} carInfo={carInfo} IS_DESKTOP={IS_DESKTOP} />
                </div>
            </CarProfileContext.Provider>
        </div>
    );
}
